/**
 * SEO Utilities for PropInvest Website
 * Generates meta tags, titles, and descriptions for property pages
 */

import { SOCIAL_LINKS, CONTACT_INFO } from '../data/siteContent';
import { getFromPrice, getPricingUnits } from './unitAvailability';
import { SITE_URL } from '../components/shared/SEOHead';

export interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  canonicalUrl?: string;
}

/**
 * Generate SEO data for property detail page
 */
export function generatePropertySEO(property: {
  name: string;
  description?: string;
  city: string;
  province: string;
  propertyType: string;
  units: Array<{ price: number; status?: string; isDeleted?: boolean }>;
}): SEOData {
  const pricingUnits = getPricingUnits(property.units);
  const minPrice = Math.min(...pricingUnits.map(u => u.price));
  const maxPrice = Math.max(...pricingUnits.map(u => u.price));

  const priceRange = minPrice === maxPrice
    ? `R${formatPrice(minPrice)}`
    : `R${formatPrice(minPrice)} - R${formatPrice(maxPrice)}`;

  const title = `${property.name} - ${property.city} Property Investment | PropInvest\u2122`;
  const description = property.description
    ? `${property.description.substring(0, 150)}... Investment from ${priceRange}. Contact us today!`
    : `Property investment in ${property.city}, ${property.province}. Investment from ${priceRange}. Contact us today!`;

  const keywords = `${property.name}, property investment, ${property.city}, ${property.province}, PropInvest\u2122, South Africa, financial freedom`;

  return {
    title,
    description,
    keywords,
    ogTitle: property.name,
    ogDescription: description
  };
}

/**
 * Generate SEO data for partner properties page
 */
export function generatePartnerSEO(partnerName: string, propertyCount: number): SEOData {
  const title = `${partnerName} Properties | PropInvest\u2122`;
  const description = `Browse ${propertyCount} properties from ${partnerName}. Find your ideal property investment in South Africa with PropInvest\u2122.`;

  return {
    title,
    description,
    ogTitle: `${partnerName} Properties`,
    ogDescription: description
  };
}

/**
 * Format price with thousands separator
 */
export function formatPrice(price: number): string {
  return price.toLocaleString('en-ZA');
}

/**
 * Generate structured data (JSON-LD) for property
 */
export function generatePropertyStructuredData(property: {
  name: string;
  slug?: string;
  id: string;
  description?: string;
  address: string;
  city: string;
  province: string;
  units: Array<{ price: number; status?: string; isDeleted?: boolean }>;
  images?: string[];
}) {
  const pricingUnits = getPricingUnits(property.units);
  const minPrice = pricingUnits.length > 0
    ? Math.min(...pricingUnits.map(u => u.price))
    : 0;
  const maxPrice = pricingUnits.length > 0
    ? Math.max(...pricingUnits.map(u => u.price))
    : 0;
  const urlPath = property.slug || property.id;

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    "name": property.name,
    "description": property.description || `${property.name} in ${property.city}`,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": property.address,
      "addressLocality": property.city,
      "addressRegion": property.province,
      "addressCountry": "ZA"
    },
    "url": `${SITE_URL}/property/${urlPath}`,
  };

  if (minPrice > 0) {
    schema["offers"] = {
      "@type": "AggregateOffer",
      "lowPrice": minPrice,
      "highPrice": maxPrice,
      "priceCurrency": "ZAR",
      "offerCount": property.units.length,
      "availability": "https://schema.org/InStock"
    };
  }

  if (property.images && property.images.length > 0) {
    schema["image"] = property.images.length === 1 ? property.images[0] : property.images;
  }

  return schema;
}

/**
 * Generate BreadcrumbList JSON-LD structured data
 */
export function generateBreadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${SITE_URL}${item.path}`
    }))
  };
}

/**
 * Organization JSON-LD structured data (injected globally)
 */
export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "PropInvest",
  "url": SITE_URL,
  "logo": `${SITE_URL}/apple-touch-icon.png`,
  "description": "PropInvest specialises in exclusive property investments, equipping South Africans with education and tools to achieve financial freedom and build generational wealth.",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Paarl",
    "addressRegion": "Western Cape",
    "addressCountry": "ZA"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+27-21-872-0028",
    "contactType": "customer service",
    "email": CONTACT_INFO.email,
    "availableLanguage": ["English", "Afrikaans"]
  },
  "sameAs": [
    SOCIAL_LINKS.facebook,
    SOCIAL_LINKS.instagram,
    SOCIAL_LINKS.twitter,
    SOCIAL_LINKS.linkedin,
    SOCIAL_LINKS.youtube
  ]
};

/**
 * Generate ItemList JSON-LD for property listing pages
 */
export function generatePropertyListSchema(properties: Array<{
  name: string;
  slug?: string;
  id: string;
  city?: string;
  province: string;
  units?: Array<{ price: number; status?: string; isDeleted?: boolean }>;
  marketingAssets?: { heroImage?: string };
}>) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": properties.map((prop, index) => {
      const urlPath = prop.slug || prop.id;
      const minPrice = getFromPrice(prop.units) ?? undefined;

      return {
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "RealEstateListing",
          "name": prop.name,
          "url": `${SITE_URL}/property/${urlPath}`,
          ...(prop.city && {
            "address": {
              "@type": "PostalAddress",
              "addressLocality": prop.city,
              "addressRegion": prop.province,
              "addressCountry": "ZA"
            }
          }),
          ...(minPrice && {
            "offers": {
              "@type": "Offer",
              "price": minPrice,
              "priceCurrency": "ZAR"
            }
          }),
          ...(prop.marketingAssets?.heroImage && {
            "image": prop.marketingAssets.heroImage
          })
        }
      };
    })
  };
}
